import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * Selector con buscador interno.
 * options: string[] | { value, label, hint?, group? }[]
 * preferred: values to show first (under "Tus preferencias")
 */
export const SearchableSelect = ({
  value,
  onChange,
  options = [],
  preferred = [],
  placeholder = "Elegir…",
  searchPlaceholder = "Buscar…",
  emptyText = "Sin resultados",
  testid,
  className,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const normalized = useMemo(() => options.map((o) => (
    typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label, hint: o.hint, group: o.group }
  )), [options]);

  const prefSet = useMemo(() => new Set(preferred.filter(Boolean)), [preferred]);
  const preferredOpts = useMemo(
    () => normalized.filter((o) => prefSet.has(o.value)),
    [normalized, prefSet],
  );
  const restOpts = useMemo(
    () => normalized.filter((o) => !prefSet.has(o.value)),
    [normalized, prefSet],
  );
  const selected = normalized.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex w-full min-w-0 max-w-full items-center justify-between gap-2 overflow-hidden rounded-2xl border border-stone-200 bg-white px-4 py-3 text-left text-sm outline-none transition-colors hover:border-stone-300 focus:border-ink disabled:opacity-50",
            className,
          )}
        >
          <span className={cn("min-w-0 flex-1 truncate", !selected && "text-stone-400")}>
            {selected ? `${selected.label}${selected.hint ? ` · ${selected.hint}` : ""}` : placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[60] w-[var(--radix-popover-trigger-width)] max-w-[min(100vw-2rem,28rem)] p-0"
        align="start"
        collisionPadding={12}
      >
        <Command className="min-w-0">
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {preferredOpts.length > 0 && (
              <CommandGroup heading="Tus preferencias">
                {preferredOpts.map((o) => (
                  <CommandItem
                    key={`pref-${o.value}`}
                    value={`${o.label} ${o.hint || ""}`}
                    onSelect={() => { onChange(o.value); setOpen(false); }}
                    className="min-w-0"
                  >
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
                    <span className="min-w-0 truncate">{o.label}{o.hint ? ` · ${o.hint}` : ""}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandGroup heading={preferredOpts.length ? "Todas las opciones" : undefined}>
              {restOpts.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.hint || ""}`}
                  onSelect={() => { onChange(o.value); setOpen(false); }}
                  className="min-w-0"
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="min-w-0 truncate">{o.label}{o.hint ? ` · ${o.hint}` : ""}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
